import { useEffect, useRef, useSyncExternalStore } from 'react'
import type { Corner, CornerSlotComputedStyle } from './CornerLayoutContext'
import {
  computeSlotStyle,
  sortSlotsForCorner,
  useCornerLayoutContext,
} from './CornerLayoutContext'
import type { CornerSlotFeature } from './cornerSlotPriority'
export interface UseFloatingCornerSlotArgs {
  corner: Corner
  // Defaults to true. When false, the slot skips registration (chain excludes it).
  enabled?: boolean
  feature: CornerSlotFeature
}

const DisabledStyle: CornerSlotComputedStyle = {}

const EmptyRef = () => {}

export function useFloatingCornerSlot({
  corner,
  enabled = true,
  feature,
}: UseFloatingCornerSlotArgs) {
  const cornerLayoutContext = useCornerLayoutContext()

  const elementRef = useRef<HTMLElement | null>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  const snapshot = useSyncExternalStore(
    cornerLayoutContext.state.store.subscribe,
    cornerLayoutContext.state.store.getSnapshot,
  )

  const { register, updateWidth } = cornerLayoutContext.actions

  useEffect(() => {
    if (enabled === false) {
      return
    }

    const unregister = register({
      corner,
      feature,
      width: elementRef.current?.getBoundingClientRect().width ?? 0,
    })
    return unregister
  }, [corner, enabled, feature, register])

  const measureAndUpdate = () => {
    if (elementRef.current === null) {
      return
    }

    const rect = elementRef.current.getBoundingClientRect()
    updateWidth({
      corner,
      feature,
      width: rect.width,
    })
  }

  const ref = (element: HTMLElement | null) => {
    elementRef.current = element

    if (resizeObserverRef.current !== null) {
      resizeObserverRef.current.disconnect()
      resizeObserverRef.current = null
    }

    if (element === null || enabled === false) {
      return
    }

    measureAndUpdate()

    const observer = new ResizeObserver(() => {
      measureAndUpdate()
    })
    observer.observe(element)
    resizeObserverRef.current = observer
  }

  useEffect(() => {
    return () => {
      resizeObserverRef.current?.disconnect()
      resizeObserverRef.current = null
    }
  }, [])

  if (enabled === false) {
    return { ref: EmptyRef, style: DisabledStyle }
  }

  const cornerSlots = sortSlotsForCorner(snapshot[corner])
  const chainRank = cornerSlots.findIndex((entry) => {
    return entry.feature === feature
  })

  if (chainRank === -1) {
    return { ref, style: DisabledStyle }
  }

  const priorSlotsWidth = cornerSlots
    .slice(0, chainRank)
    .reduce((sum, entry) => {
      return sum + entry.width
    }, 0)

  const style = computeSlotStyle({
    chainLength: cornerSlots.length,
    chainRank,
    corner,
    offsets: cornerLayoutContext.meta.offsets,
    priorSlotsWidth,
  })

  return { ref, style }
}
